import type { SagaReturnType } from 'redux-saga/effects';
import { takeLatest, all, select, call, put } from 'redux-saga/effects';
import { addDays, getTime } from 'date-fns';
import { getIsMobile, searchTypeSelector } from 'src/store/view/selectors';
import { getLocalizationValues } from '@lt/localization/dist/utils/getValuesFromBackend';
import {
  convertClientDateToServerDate,
  formatServerDate,
} from 'src/utils/dateUtils';
import {
  getFiltersSelected,
  getReviewsCount,
  getReviewsPage,
  getSorting,
} from 'src/store/reviews/selectors';
import { getRequestId } from 'src/routes/SearchPage/selectors/searchParams';
import { getSortingType } from 'src/store/reviews/utils';
import {
  changeFilterStatus,
  setSorting,
  showHotelReviewsAction,
  showMoreReviews,
} from 'src/store/reviews/slice';
import { getRecommenderType } from 'src/utils/featureToggleSigma/analytics/abNewRecommender';

import { sendMindboxPageviewHotelAction } from 'src/store/mindbox/actions';
import {
  getHotelName,
  getHotelId,
  getHotelStars,
  getHotelCoordinates,
  getHotelLabels,
} from 'src/routes/Hotel/store/hotel';
import { takeOnce } from '../../../../helpers/effects';
import { dataLayerPush } from '../../../../helpers/gtm';

import { INIT_APP } from '../../../../store/view/constants';
import { SHOW_PAGE } from '../constants/view';
import {
  FILTERS_SUBMIT_CLICKED,
  SET_EMPTY_SEARCHER,
  SET_SEARCHER_CAROUSEL_DATE,
  STOP_SEARCH,
} from '../constants/searcher';
import { OPEN_OFFER } from '../constants/offer';
import { COMMIT_SEARCH_PARAMS } from '../constants/searchParams';
import {
  getSearchParamsFromCity,
  getSearchParamsToCountry,
  getSearchParamsToCityRu,
  getSearchParamsStartDate,
  getSearchParamsNights,
  getSearchParamsAdults,
  getSearchParamsKidsCount,
  getSearchParamsKidsAges,
  getSearchParamsToCountryNameEn,
  getSearchParamsToCountryId,
  getSearchParamsToCityId,
  getSearchParamsToCityEn,
} from '../../../../store/enqueue/selectors';
import { getNightsRange } from '../../../../utils/searchParamsUtils';
import {
  getAnalyticsOperators,
  getOffers,
  selectHotelFiltersAnalyticsData,
} from '../selectors/analytics';
import {
  getDefaultPrice,
  minAnalyticsPriceSelector,
} from '../selectors/common';
import {
  getSearcherNights,
  getSearcherStartDate,
  getSearcherTouristsCount,
} from '../selectors/searcher';
import { SHOW_MORE_ROOMS } from '../../constants';

const CATEGORY = {
  HOTEL_PAGE: 'hotel_page',
  HOTEL_SEARCH: 'hotel_search',
};

/**
 * Собирает данные для события ccx-pageview
 */
function* collectCcxPageviewData() {
  const hotelId = yield select(getHotelId);
  const countryId = yield select(getSearchParamsToCountryId);
  const countryName = yield select(getSearchParamsToCountryNameEn);

  const fromCity = yield select(getSearchParamsFromCity);
  const startDate = yield select(getSearchParamsStartDate);
  const nights = yield select(getSearchParamsNights);
  const [nightsFrom, nightsTo] = getNightsRange(nights);
  const adults = yield select(getSearchParamsAdults);
  const kidsCount = yield select(getSearchParamsKidsCount);
  const kidsAges = yield select(getSearchParamsKidsAges);
  const stars = yield select(getHotelStars);
  const coordinates = yield select(getHotelCoordinates);
  const { lat, long } = coordinates || {};
  const hotelName = yield select(getHotelName);
  const cityId = yield select(getSearchParamsToCityId);
  const city = yield select(getSearchParamsToCityEn);
  const reviewsCount = yield select(getReviewsCount);
  const labels = yield select(getHotelLabels);
  const searchType = yield select(searchTypeSelector);
  const minPrice = yield select(minAnalyticsPriceSelector);
  const localization = getLocalizationValues();

  const variants = yield select(getOffers);

  const operators = yield select(getAnalyticsOperators);

  const timestamp = getTime(new Date(startDate));

  return {
    adults_count: adults,
    country_id: countryId,
    country_name: countryName,
    departure_name: fromCity,
    id: hotelId,
    kids_ages: kidsAges, // FIXME: в доке нет этого поля
    kids_count: kidsCount,
    latitude: Number(lat),
    longitude: Number(long),
    name: hotelName,
    nights_from: nightsFrom,
    nights_to: nightsTo,
    rating: null, // TODO: какой рейтинг передавать?
    reviews_count: reviewsCount,
    stars,
    start_timestamp: timestamp,
    to_place_id: Number(cityId),
    to_place_name: city,
    variants,
    labels: labels?.length ? labels.map((label) => label.id).join(';') : null,
    search_type: searchType,
    min_price: minPrice,
    currency_code: localization.currencyCode,
    operators,
  };
}

/**
 * Собирает данные для hotel_params
 */
export function* collectHotelParamsData() {
  const hotelId = yield select(getHotelId);
  const page = window.location.href; // наверно нужен актуальный url
  const fromCity = yield select(getSearchParamsFromCity);
  const toCountryIso2 = yield select(getSearchParamsToCountry);
  const toCityRu = yield select(getSearchParamsToCityRu);
  const startDate = yield select(getSearchParamsStartDate);
  const nights = yield select(getSearchParamsNights);
  const adults = yield select(getSearchParamsAdults);
  const kidsCount = yield select(getSearchParamsKidsCount);
  const kidsAges = yield select(getSearchParamsKidsAges); // массив строк ???
  const stars = yield select(getHotelStars);
  const minPrice = yield select(getDefaultPrice);
  const searchType = yield select(searchTypeSelector);
  // TODO: проверить корректность математического действия, оно перенесено просто со старого отеля
  const pricePerNightPerPerson = minPrice
    ? Math.round(minPrice / (adults + kidsCount) / nights)
    : 0;

  return {
    hotel_id: hotelId,
    flex_dates: true,
    page,
    departure_city: fromCity,
    country: toCountryIso2,
    region: toCityRu,
    flight_date: startDate,
    nights_count: nights,
    adults_count: adults,
    kids_count: kidsCount,
    kids_ages: kidsAges,
    stars,
    min_price: minPrice,
    seen_price_per_night_person: pricePerNightPerPerson,
    search_type: searchType,
  };
}

function pushInitialized() {
  dataLayerPush({
    event: 'initizialized',
    eventCategory: CATEGORY.HOTEL_PAGE,
    eventLabel: 'Hotel page inited',
  });
}

/**
 * Отправляет событие ccx-pageview
 */
function* pushCcxPageview() {
  try {
    const data = yield collectCcxPageviewData();

    const isMobile = yield select(getIsMobile);
    const searchType = yield select(searchTypeSelector);

    const { group }: SagaReturnType<typeof getRecommenderType> = yield call(
      getRecommenderType,
      isMobile,
      searchType,
    );

    dataLayerPush({
      event: 'ccx-pageview',
      hotel_params: data,
      custom_params: {
        recommenderGroup: group,
      },
    });

    yield put(sendMindboxPageviewHotelAction());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Can not push ccx-pageview', error);
  }
}

/**
 * Изменение даты в карусели
 */
function onChangeCarouselDate() {
  dataLayerPush({
    event: 'switch_date',
    eventCategory: CATEGORY.HOTEL_SEARCH,
  });
}

/**
 * Открытие пакета
 */
function onOpenPackage() {
  dataLayerPush({
    event: 'open_package',
    eventCategory: CATEGORY.HOTEL_SEARCH,
  });
}

/**
 * Отправляет событие hotel_params
 */
function* pushHotelParams() {
  try {
    const eventParams = yield collectHotelParamsData();
    dataLayerPush({
      event: 'hotel_params',
      eventCategory: 'hotel_page',
      eventLabel: 'End of product details',
      eventParams,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Can not push hotel_params', error);
  }
}

/**
 * Отправляет событие ccx-event
 */
function* pushCcxEvent() {
  try {
    const data = yield collectCcxPageviewData();
    dataLayerPush({
      event: 'ccx-event',
      hotel_params: data,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Can not push ccx-event', error);
  }
}

function* onStopSearch() {
  yield pushHotelParams();
  yield pushCcxEvent();
}

/**
 * Старт приложения
 */
function* onInitApp() {
  pushInitialized();
  yield pushCcxPageview();
  yield takeOnce(STOP_SEARCH, onStopSearch);
}

/**
 * Коммит поисковых параметров и запуск поиска
 * - Нажатие кнопки "Найти"
 * - Нажатие "Узнать цену"
 */
function* onChangeSearchParams() {
  yield takeOnce(STOP_SEARCH, onStopSearch);
}

/**
 * Отправляет события при переходе по страницам
 */
function onChangePage(action) {
  const { payload } = action;
  const eventNames = {
    searcher: 'prices',
    reviews: 'reviews',
    map: 'map',
  };
  const event = payload ? eventNames[payload] : 'back_to_hotel';
  dataLayerPush({
    event,
    eventCategory: CATEGORY.HOTEL_PAGE,
  });
}

/**
 * Отправляет событие no_result при пустой выдаче
 */
function* onEmptySearcherLoad() {
  const hotelName = yield select(getHotelName);
  const startDate = yield select(getSearcherStartDate);
  const convertStartDate = convertClientDateToServerDate(startDate);
  const nights = yield select(getSearcherNights);
  const endDate = addDays(new Date(convertStartDate), nights);
  const tourists = yield select(getSearcherTouristsCount);
  const request_id = yield select(getRequestId);

  dataLayerPush({
    event: 'no_results_on_hotel_page',
    no_result_params: {
      name: hotelName,
      start_date: convertStartDate,
      end_date: formatServerDate(endDate),
      tourists,
      url: window.location.href,
      request_id,
    },
  });
}

/**
 * Просмотр страницы/экрана отзывов
 */
function* handleShowHotelReviews() {
  const data: SagaReturnType<typeof collectCcxPageviewData> = yield call(
    collectCcxPageviewData,
  );
  dataLayerPush({
    event: 'hotel_reviews',
    hotel_reviews_params: { path: window.location.href, ...data },
  });
}

/**
 * Отправляет примененные фильтры на отзывах
 */
function* changeHotelReviewsFilters() {
  const data: SagaReturnType<typeof collectCcxPageviewData> = yield call(
    collectCcxPageviewData,
  );
  const selectedFilters: SagaReturnType<typeof getFiltersSelected> =
    yield select(getFiltersSelected);
  dataLayerPush({
    event: 'hotel_reviews_filter_changed',
    hotel_reviews_filter_changed_params: {
      custom_params: selectedFilters,
      hotel_params: { path: window.location.href, ...data },
    },
  });
}

/**
 * Отправляет примененные сортировки на отзывах
 */
function* changeHotelReviewsSorting() {
  const data: SagaReturnType<typeof collectCcxPageviewData> = yield call(
    collectCcxPageviewData,
  );
  const sortType = getSortingType(yield select(getSorting));
  dataLayerPush({
    event: 'hotel_reviews_sort_changed',
    hotel_reviews_sort_changed_params: {
      hotel_params: { path: window.location.href, ...data },
      custom_params: sortType,
    },
  });
}

/**
 * Нажатие на кнопку "Показать еще"
 */
function* handleShowMoreHotelReviews() {
  const data: SagaReturnType<typeof collectCcxPageviewData> = yield call(
    collectCcxPageviewData,
  );
  const pageNumber: SagaReturnType<typeof getReviewsPage> = yield select(
    getReviewsPage,
  );
  dataLayerPush({
    event: 'hotel_reviews_show_more',
    hotel_reviews_show_more_params: {
      hotel_params: { path: window.location.href, ...data },
      custom_params: { page_number: pageNumber },
    },
  });
}

/**
 * Нажатие на кнопку "Готово" в фильтрах
 */
function* onChooseFilters() {
  const hotelParams = yield select(selectHotelFiltersAnalyticsData);
  const regex = /request_id=\w*/;
  const newUrl = window.location.href.replace(regex, '');
  yield call(dataLayerPush, {
    event: 'hotel_filters_used',
    hotel_filters_used_params: {
      url: newUrl,
      hotel_params: hotelParams,
    },
  });
}

/**
 * Нажатие на кнопку "Показать ещё N вариантов" на странице отеля
 */
function onShowMoreRoomsClicked() {
  dataLayerPush({
    event: 'show_more_rooms',
    show_more_rooms_params: {
      custom_params: {
        search_type: 'package',
      },
    },
  });
}

export default function* HotelAnalyticsWatcher() {
  yield all([
    takeOnce(INIT_APP, onInitApp),
    takeLatest(FILTERS_SUBMIT_CLICKED, onChooseFilters),
    takeLatest(SET_SEARCHER_CAROUSEL_DATE, onChangeCarouselDate),
    takeLatest(OPEN_OFFER, onOpenPackage),
    takeLatest(COMMIT_SEARCH_PARAMS, onChangeSearchParams),
    takeLatest(SHOW_PAGE, onChangePage),
    takeLatest(SET_EMPTY_SEARCHER, onEmptySearcherLoad),
    takeLatest(changeFilterStatus, changeHotelReviewsFilters),
    takeLatest(setSorting, changeHotelReviewsSorting),
    takeLatest(showHotelReviewsAction, handleShowHotelReviews),
    takeLatest(showMoreReviews, handleShowMoreHotelReviews),
    takeLatest(SHOW_MORE_ROOMS, onShowMoreRoomsClicked),
  ]);
}
